import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const API_URL = '/api/affiliate/application'

export type ApplicationStatus = 'pending' | 'approved' | 'rejected' | null

export type ApplicationKind = 'creator' | 'manager'

export type AffiliateApplication = {
    status: ApplicationStatus
    kind?: ApplicationKind
    /** The agency this application is attached to, once submitted. */
    manager?: string | null
    /** The agency whose recruiting link is currently in play, before submitting. */
    recruiting_manager?: string | null
    audience?: string
    audience_size?: string
    pitch?: string
    created_at?: string
    reviewed_at?: string | null
}

export type ApplicationDraft = {
    audience: string
    audience_size: string
    pitch: string
    kind: ApplicationKind
}

/**
 * Both roles, always.
 *
 * Creator and agency are separately applied for, so "have I applied" has two
 * answers. Returning one status is what made an approved creator unable to
 * discover, let alone apply for, the agency role.
 */
export type ApplicationSet = {
    creator: AffiliateApplication
    manager: AffiliateApplication
    recruiting_manager?: string | null
}

const EMPTY_SET: ApplicationSet = { creator: { status: null }, manager: { status: null } }

export const useAffiliateApplication = (enabled = true) =>
    useQuery<ApplicationSet>({
        queryKey: [API_URL],
        queryFn: async () => {
            const res = await fetch(API_URL)
            if (!res.ok) return EMPTY_SET
            return res.json()
        },
        retry: false,
        enabled,
    })

/**
 * Submitting an application.
 *
 * Raw `fetch` rather than the shared `apiClient`, which parses the body without
 * looking at the status — so a 400 carrying `{error: "..."}` would resolve as a
 * success and the form would clear itself and claim to have been sent. The
 * server's message is surfaced verbatim because it is the specific one (too
 * long, missing, already approved) rather than a generic failure.
 */
export const useSubmitAffiliateApplication = () => {
    const queryClient = useQueryClient()

    return useMutation<AffiliateApplication, Error, ApplicationDraft>({
        mutationFn: async (draft) => {
            const csrf = document.cookie
                .split(';')
                .map((c) => c.trim().split('='))
                .find(([key]) => key === 'csrftoken')?.[1]

            const res = await fetch(API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(csrf ? { 'X-CSRFToken': decodeURIComponent(csrf) } : {}),
                },
                body: JSON.stringify(draft),
            })

            const body = await res.json().catch(() => ({}))
            if (!res.ok) throw new Error(body.error || 'Your application could not be sent.')
            return body
        },
        // Refetch rather than patch: one submission changes one role's slot, and
        // writing the single application back over the pair would erase the other.
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [API_URL] }),
    })
}
